import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import ImageURL from '../../ImageURL';
import { parseRetreatImages, renderTextPreview } from './retreatAdminUtils';
import './RetreatManage.scss';

export interface ReviewRow {
  id: number;
  title: string;
  content: string;
  userAccount: string;
  userNickName: string;
  date: string;
  views: number;
  commentCount?: number;
  images: string | string[] | null;
}

interface ReviewComment {
  id: number;
  post_id: number;
  content: string;
  userAccount: string;
  userNickName: string;
  date: string;
}

type EditForm = {
  title: string;
  content: string;
  userAccount: string;
};

const emptyForm: EditForm = {
  title: '',
  content: '',
  userAccount: '',
};

export default function ReviewManage() {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchWord, setSearchWord] = useState('');
  const [editRow, setEditRow] = useState<ReviewRow | null>(null);
  const [form, setForm] = useState<EditForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);
  const [editImageNames, setEditImageNames] = useState<string[]>([]);
  const [dragOverImageIndex, setDragOverImageIndex] = useState<number | null>(null);
  const dragImageIndexRef = useRef<number | null>(null);
  const [commentRow, setCommentRow] = useState<ReviewRow | null>(null);
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post<{ ok: boolean; data: ReviewRow[] }>(
        `${MainURL}/retreatreview/admingetall`
      );
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error(error);
      alert('후기 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const word = searchWord.trim().toLowerCase();
    return rows.filter((row) => {
      if (!word) return true;
      const haystack = [row.title, row.content, row.userNickName, row.userAccount]
        .join(' ')
        .toLowerCase();
      return haystack.includes(word);
    });
  }, [rows, searchWord]);

  const openEdit = (row: ReviewRow) => {
    setEditRow(row);
    setForm({
      title: row.title || '',
      content: row.content || '',
      userAccount: row.userAccount || '',
    });
    setEditImageNames(parseRetreatImages(row.images));
    setDragOverImageIndex(null);
    dragImageIndexRef.current = null;
  };

  const closeEdit = () => {
    setEditRow(null);
    setForm(emptyForm);
    setEditImageNames([]);
    setDragOverImageIndex(null);
    dragImageIndexRef.current = null;
  };

  const handleImageDragStart = (index: number) => {
    dragImageIndexRef.current = index;
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverImageIndex(index);
  };

  const handleImageDrop = (dropIndex: number) => {
    const fromIndex = dragImageIndexRef.current;
    if (fromIndex === null || fromIndex === dropIndex) {
      setDragOverImageIndex(null);
      return;
    }
    setEditImageNames((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    dragImageIndexRef.current = null;
    setDragOverImageIndex(null);
  };

  const handleImageDragEnd = () => {
    dragImageIndexRef.current = null;
    setDragOverImageIndex(null);
  };

  const removeEditImage = (fileName: string) => {
    if (!window.confirm('이 사진을 삭제하시겠습니까?')) return;
    setEditImageNames((prev) => prev.filter((name) => name !== fileName));
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!form.title.trim() || !form.content.trim()) {
      alert('제목과 내용은 필수입니다.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${MainURL}/retreatreview/adminupdate`, {
        id: editRow.id,
        title: form.title.trim(),
        content: form.content.trim(),
        userAccount: form.userAccount.trim(),
        images: JSON.stringify(editImageNames),
      });
      if (res.data) {
        alert('저장되었습니다.');
        closeEdit();
        void fetchRows();
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row: ReviewRow) => {
    if (!window.confirm(`「${row.title}」 후기를 삭제하시겠습니까?`)) return;
    setActingId(row.id);
    try {
      const res = await axios.post(`${MainURL}/retreatreview/admindelete`, { id: row.id });
      if (res.data) {
        setRows((prev) => prev.filter((item) => item.id !== row.id));
        if (editRow?.id === row.id) closeEdit();
        if (commentRow?.id === row.id) closeComments();
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setActingId(null);
    }
  };

  const openComments = async (row: ReviewRow) => {
    setCommentRow(row);
    setCommentsLoading(true);
    try {
      const res = await axios.get(`${MainURL}/retreatreview/getallcomments/${row.id}`);
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      alert('댓글을 불러오지 못했습니다.');
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const closeComments = () => {
    setCommentRow(null);
    setComments([]);
  };

  const deleteComment = async (comment: ReviewComment) => {
    if (!commentRow) return;
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) return;

    try {
      const res = await axios.post(`${MainURL}/retreatreview/admincommentdelete`, {
        commentId: comment.id,
        postId: commentRow.id,
      });
      if (res.data) {
        setComments((prev) => prev.filter((item) => item.id !== comment.id));
        setRows((prev) =>
          prev.map((item) =>
            item.id === commentRow.id
              ? { ...item, commentCount: Math.max(0, (item.commentCount ?? 0) - 1) }
              : item
          )
        );
      } else {
        alert('댓글 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('댓글 삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="retreat-admin">
      <div className="retreat-admin__toolbar">
        <input
          className="retreat-admin__search"
          type="text"
          placeholder="제목, 내용, 작성자 검색"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
        />
        <button type="button" className="retreat-admin__refresh" onClick={() => void fetchRows()}>
          새로고침
        </button>
        <a className="retreat-admin__link" href="/retreat/review" target="_blank" rel="noreferrer">
          사이트 목록 보기
        </a>
      </div>

      {loading ? (
        <div className="retreat-admin__loading">불러오는 중...</div>
      ) : filteredRows.length === 0 ? (
        <div className="retreat-admin__empty">표시할 후기가 없습니다.</div>
      ) : (
        <div className="retreat-admin__table-wrap">
          <table className="retreat-admin__table">
            <thead>
              <tr>
                <th>ID</th>
                <th>사진</th>
                <th>제목</th>
                <th>작성자</th>
                <th>계정</th>
                <th>조회</th>
                <th>댓글</th>
                <th>등록일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const images = parseRetreatImages(row.images);
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>
                      {images[0] ? (
                        <img
                          className="retreat-admin__thumb"
                          src={`${ImageURL}/images/retreat/postimage/${images[0]}`}
                          alt=""
                        />
                      ) : (
                        '-'
                      )}
                    </td>
                    <td title={row.title}>{renderTextPreview(row.title)}</td>
                    <td>{row.userNickName}</td>
                    <td title={row.userAccount}>{renderTextPreview(row.userAccount, 24)}</td>
                    <td>{row.views}</td>
                    <td>{row.commentCount ?? 0}</td>
                    <td>{row.date ? String(row.date).slice(0, 10) : '-'}</td>
                    <td>
                      <div className="retreat-admin__actions">
                        <button
                          type="button"
                          className="retreat-admin__btn retreat-admin__btn--ghost"
                          onClick={() => openEdit(row)}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          className="retreat-admin__btn retreat-admin__btn--ghost"
                          onClick={() => void openComments(row)}
                        >
                          댓글
                        </button>
                        <button
                          type="button"
                          className="retreat-admin__btn retreat-admin__btn--danger"
                          disabled={actingId === row.id}
                          onClick={() => void deleteRow(row)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editRow && (
        <div className="retreat-admin__modal-backdrop" onClick={closeEdit}>
          <div className="retreat-admin__modal" onClick={(e) => e.stopPropagation()}>
            <div className="retreat-admin__modal-head">
              <h3>후기 수정 — {editRow.title}</h3>
              <button type="button" className="retreat-admin__modal-close" onClick={closeEdit}>
                ×
              </button>
            </div>
            <div className="retreat-admin__modal-body">
              {editImageNames.length > 0 && (
                <div className="retreat-admin__field">
                  <label>사진 순서</label>
                  <p className="retreat-admin__preview-hint">
                    사진을 드래그해 순서를 바꿀 수 있습니다. 첫 번째 사진이 목록·상세의 대표 이미지입니다.
                  </p>
                  <div className="retreat-admin__preview-images retreat-admin__preview-images--sortable">
                    {editImageNames.map((img, index) => (
                      <div
                        key={img}
                        className={`retreat-admin__preview-item${
                          dragOverImageIndex === index ? ' is-drag-over' : ''
                        }${index === 0 ? ' is-cover' : ''}`}
                        draggable
                        onDragStart={() => handleImageDragStart(index)}
                        onDragOver={(e) => handleImageDragOver(e, index)}
                        onDrop={() => handleImageDrop(index)}
                        onDragEnd={handleImageDragEnd}
                      >
                        {index === 0 && <span className="retreat-admin__preview-badge">대표</span>}
                        <span className="retreat-admin__preview-order">{index + 1}</span>
                        <button
                          type="button"
                          className="retreat-admin__preview-remove"
                          title="사진 삭제"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEditImage(img);
                          }}
                        >
                          ×
                        </button>
                        <img src={`${ImageURL}/images/retreat/postimage/${img}`} alt="" draggable={false} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="retreat-admin__field">
                <label htmlFor="review-title">제목</label>
                <input
                  id="review-title"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="retreat-admin__field">
                <label htmlFor="review-content">내용</label>
                <textarea
                  id="review-content"
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                />
              </div>
              <div className="retreat-admin__field">
                <label>작성자 닉네임</label>
                <input value={editRow.userNickName} readOnly />
              </div>
              <div className="retreat-admin__field">
                <label htmlFor="review-userAccount">userAccount</label>
                <input
                  id="review-userAccount"
                  value={form.userAccount}
                  onChange={(e) => setForm((prev) => ({ ...prev, userAccount: e.target.value }))}
                  placeholder="로그인 계정"
                />
              </div>
            </div>
            <div className="retreat-admin__modal-foot">
              <button
                type="button"
                className="retreat-admin__btn retreat-admin__btn--ghost"
                onClick={closeEdit}
              >
                취소
              </button>
              <button
                type="button"
                className="retreat-admin__btn retreat-admin__btn--primary"
                disabled={saving}
                onClick={() => void saveEdit()}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {commentRow && (
        <div className="retreat-admin__modal-backdrop" onClick={closeComments}>
          <div className="retreat-admin__modal" onClick={(e) => e.stopPropagation()}>
            <div className="retreat-admin__modal-head">
              <h3>댓글 관리 — {commentRow.title}</h3>
              <button type="button" className="retreat-admin__modal-close" onClick={closeComments}>
                ×
              </button>
            </div>
            <div className="retreat-admin__modal-body">
              {commentsLoading ? (
                <div className="retreat-admin__loading">댓글 불러오는 중...</div>
              ) : comments.length === 0 ? (
                <div className="retreat-admin__empty">등록된 댓글이 없습니다.</div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="retreat-admin__comment-item">
                    <div className="retreat-admin__comment-meta">
                      <strong>{comment.userNickName}</strong>
                      <span>{comment.date ? String(comment.date).slice(0, 10) : '-'}</span>
                    </div>
                    <p>{comment.content}</p>
                    <button
                      type="button"
                      className="retreat-admin__btn retreat-admin__btn--danger"
                      onClick={() => void deleteComment(comment)}
                    >
                      삭제
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="retreat-admin__modal-foot">
              <button
                type="button"
                className="retreat-admin__btn retreat-admin__btn--ghost"
                onClick={closeComments}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
